import type {
  PricingRule,
  PricingContext,
  PricingResult,
  AppliedRule,
  PricingRuleType,
  DiscountType,
  ModificationAction,
  TieredPricingConfig,
  TieredBreakdownLine,
  VolumeDiscountConfig,
  MultiSessionConfig,
  GroupSizeConfig,
  EarlyBirdConfig,
  LastMinuteConfig,
  PeakTimeConfig,
  MemberPricingConfig,
  DepositConfig,
  SlidingScaleConfig,
  BogoConfig,
  CapacityBasedPricingConfig,
  SessionBundleConfig,
  RentalFractionTier,
} from "./types";
import { generateBreakdown } from "./priceBreakdown";
import { evaluateConditionGroups, hasEvaluableConditions } from "./ConditionEvaluator";

type RuleEvalResult =
  | { applies: false }
  | {
      applies: true;
      newPrice: number;
      exactTotal?: number;
      /** Tickets the price charges, when fewer than ctx.quantity (tiered cap). */
      chargedQuantity?: number;
      tieredBreakdown?: TieredBreakdownLine[];
      isCustomerChoice?: boolean;
      slidingScaleConfig?: SlidingScaleConfig;
    };

type DepositEvalResult =
  | { applies: false }
  | { applies: true; depositAmount: number; remainderDue: string };

const ELIGIBILITY_TYPES = new Set<PricingRuleType>([]);
const PRICE_OVERRIDE_TYPES = new Set(["TIERED_PRICING", "GROUP_SIZE", "SLIDING_SCALE", "CAPACITY_BASED_PRICING", "RENTAL_FRACTION_TIERS"]);
const DISCOUNT_TYPES = new Set(["VOLUME_DISCOUNT", "MULTI_SESSION", "EARLY_BIRD", "LAST_MINUTE", "PEAK_TIME", "MEMBER_PRICING", "BOGO"]);
const PAYMENT_TYPES = new Set(["DEPOSIT"]);

function isWithinValidityWindow(rule: PricingRule, bookingTime: Date): boolean {
  if (rule.validFrom) {
    const from = new Date(rule.validFrom);
    if (bookingTime < from) return false;
  }
  if (rule.validUntil) {
    const until = new Date(rule.validUntil);
    if (bookingTime > until) return false;
  }
  return true;
}

function applyDiscountToPrice(
  basePrice: number,
  discountType: "percentage" | "flat" | "fixedPrice" | "pricePerUnit" | "pricePerSession",
  discountValue: number,
): number {
  switch (discountType) {
    case "percentage":
      return Math.round(basePrice * (1 - discountValue / 100));
    case "flat":
      return Math.max(0, basePrice - discountValue);
    case "fixedPrice":
      return discountValue;
    case "pricePerUnit":
    case "pricePerSession":
      return discountValue;
    default:
      return basePrice;
  }
}

function evaluateTieredPricing(config: TieredPricingConfig, _basePrice: number, ctx: PricingContext): RuleEvalResult {
  if (ctx.quantity < config.minTickets) return { applies: false };
  const effectiveQty = config.maxTickets ? Math.min(ctx.quantity, config.maxTickets) : ctx.quantity;
  const baseQty = config.minTickets;
  const extraQty = effectiveQty - baseQty;
  const baseTotal = config.basePrice;
  const extrasTotal = extraQty * config.incrementPrice;
  const totalCost = baseTotal + extrasTotal;
  const tieredBreakdown: TieredBreakdownLine[] = [];
  if (baseQty > 0) {
    tieredBreakdown.push({
      unitPrice: Math.round(baseTotal / baseQty),
      quantity: baseQty,
      lineTotal: baseTotal,
    });
  }
  if (extraQty > 0) {
    tieredBreakdown.push({
      unitPrice: config.incrementPrice,
      quantity: extraQty,
      lineTotal: extrasTotal,
    });
  }
  return {
    applies: true,
    newPrice: effectiveQty > 0 ? Math.round(totalCost / effectiveQty) : config.basePrice,
    exactTotal: totalCost,
    chargedQuantity: effectiveQty,
    tieredBreakdown,
  };
}

function evaluateVolumeDiscount(config: VolumeDiscountConfig, basePrice: number, ctx: PricingContext): RuleEvalResult {
  const sortedThresholds = [...config.thresholds].sort((a, b) => b.minQuantity - a.minQuantity);
  const matchingTier = sortedThresholds.find((t) => ctx.quantity >= t.minQuantity);
  if (!matchingTier) return { applies: false };
  return { applies: true, newPrice: applyDiscountToPrice(basePrice, matchingTier.discountType, matchingTier.discountValue) };
}

function evaluateRentalFractionTiers(
  tiers: RentalFractionTier[] | undefined,
  _basePrice: number,
  ctx: PricingContext,
): RuleEvalResult {
  // Only `priceRentalHire` sets `fractions`, and the rule must also be
  // attached to the ticket — so this is unreachable in practice for
  // appointment, regular, term, trial and drop-in tickets. It is a
  // convention, not a type-level guarantee: if you add another caller that
  // sets `fractions`, this rule becomes live for it.
  const fractions = ctx.fractions;
  if (typeof fractions !== "number" || !Number.isFinite(fractions) || fractions < 1) return { applies: false };
  if (!tiers?.length) return { applies: false };

  // Highest rung at or below the chosen fraction count. Below the lowest rung
  // nothing matches and the caller's base price stands — same shape as
  // evaluateVolumeDiscount, which also declines rather than inventing a tier.
  // A malformed or negative price disqualifies the rung entirely: a lower
  // valid rung (or the base price) wins rather than charging nothing.
  const matching = tiers
    .filter((t) => Number.isFinite(t.minFractions) && Number.isFinite(t.price) && t.price >= 0 && fractions >= t.minFractions)
    .sort((a, b) => b.minFractions - a.minFractions)[0];
  if (!matching) return { applies: false };

  const unitPrice =
    matching.priceMode === "flat"
      ? matching.price
      : matching.price * fractions;
  const newPrice = Math.max(0, Math.round(unitPrice));
  // `newPrice` prices ONE hired unit; `ctx.quantity` is how many units are
  // being hired. Without an explicit exactTotal (evaluateTieredPricing sets
  // one for the same reason), generateBreakdown's single tieredBreakdown
  // line described one unit while the Total line covered every unit — the
  // lines summed to a fraction of the total whenever more than one unit was
  // hired.
  const exactTotal = newPrice * ctx.quantity;

  const tieredBreakdown: TieredBreakdownLine[] = [
    { unitPrice: newPrice, quantity: ctx.quantity, lineTotal: exactTotal },
  ];

  return { applies: true, newPrice, exactTotal, tieredBreakdown };
}

function evaluateMultiSession(config: MultiSessionConfig, basePrice: number, ctx: PricingContext): RuleEvalResult {
  if (ctx.sessionsCount < config.minSessions) return { applies: false };
  return { applies: true, newPrice: applyDiscountToPrice(basePrice, config.discountType, config.discountValue) };
}

function evaluateGroupSize(config: GroupSizeConfig, _basePrice: number, ctx: PricingContext): RuleEvalResult {
  const matchingTier = config.tiers.find(
    (t) => ctx.totalTicketsInCart >= t.minSize && ctx.totalTicketsInCart <= t.maxSize,
  );
  if (!matchingTier) return { applies: false };
  return { applies: true, newPrice: matchingTier.pricePerPerson };
}

function evaluateEarlyBird(config: EarlyBirdConfig, basePrice: number, ctx: PricingContext): RuleEvalResult {
  const sessionTime = new Date(ctx.sessionStartTime).getTime();
  const bookingTime = new Date(ctx.bookingTime).getTime();

  let deadlineTime: number;
  if (config.deadlineType === "daysBeforeEvent") {
    deadlineTime = sessionTime - (config.deadlineValue as number) * 24 * 60 * 60 * 1000;
  } else {
    deadlineTime = new Date(config.deadlineValue as string).getTime();
  }

  if (bookingTime > deadlineTime) return { applies: false };
  return { applies: true, newPrice: applyDiscountToPrice(basePrice, config.discountType, config.discountValue) };
}

function evaluateLastMinute(config: LastMinuteConfig, basePrice: number, ctx: PricingContext): RuleEvalResult {
  const sessionTime = new Date(ctx.sessionStartTime).getTime();
  const bookingTime = new Date(ctx.bookingTime).getTime();
  const hoursUntilEvent = (sessionTime - bookingTime) / (1000 * 60 * 60);

  if (hoursUntilEvent > config.hoursBeforeEvent || hoursUntilEvent < 0) return { applies: false };
  const newPrice = applyDiscountToPrice(basePrice, config.discountType, config.discountValue);
  return { applies: true, newPrice: Math.max(newPrice, config.minPrice ?? 0) };
}

function evaluatePeakTime(config: PeakTimeConfig, basePrice: number, ctx: PricingContext): RuleEvalResult {
  const matchingRule = config.rules.find((r) => {
    if (!r.daysOfWeek.includes(ctx.sessionDayOfWeek)) return false;
    if (r.timeStart && r.timeEnd) {
      return ctx.sessionTimeOfDay >= r.timeStart && ctx.sessionTimeOfDay <= r.timeEnd;
    }
    return true;
  });
  if (!matchingRule) return { applies: false };

  const modifier = matchingRule.modifierType === "surcharge" ? 1 : -1;
  const amount =
    matchingRule.valueType === "percentage"
      ? Math.round((basePrice * matchingRule.value) / 100)
      : matchingRule.value;
  return { applies: true, newPrice: Math.max(0, basePrice + modifier * amount) };
}

function evaluateMemberPricing(config: MemberPricingConfig, basePrice: number, ctx: PricingContext): RuleEvalResult {
  const hasTag =
    config.matchType === "any"
      ? config.requiredTags.some((t) => ctx.customerTags.includes(t))
      : config.requiredTags.every((t) => ctx.customerTags.includes(t));
  if (!hasTag) return { applies: false };
  return { applies: true, newPrice: applyDiscountToPrice(basePrice, config.discountType, config.discountValue) };
}

function evaluateSlidingScale(config: SlidingScaleConfig, _basePrice: number, ctx: PricingContext): RuleEvalResult {
  const chosenPrice = ctx.customerChosenPrice ?? config.suggestedPrice;
  const clampedPrice = Math.max(
    config.allowZero ? 0 : config.minPrice,
    Math.min(chosenPrice, config.maxPrice ?? Infinity),
  );
  return { applies: true, newPrice: clampedPrice, isCustomerChoice: true, slidingScaleConfig: config };
}

function evaluateBogo(config: BogoConfig, basePrice: number, ctx: PricingContext): RuleEvalResult {
  if (!ctx.cart) return { applies: false };

  const triggerItem = ctx.cart.find((c) => c.ticketTypeId === config.triggerTicketId);
  if (!triggerItem || triggerItem.quantity < config.triggerQuantity) return { applies: false };

  const timesTriggered = config.repeatable
    ? Math.floor(triggerItem.quantity / config.triggerQuantity)
    : 1;
  const discountedQty = Math.min(timesTriggered * config.discountedQuantity, ctx.quantity);
  const fullPriceQty = ctx.quantity - discountedQty;

  const discountedUnitPrice = applyDiscountToPrice(basePrice, config.discountType, config.discountValue);
  const blendedTotal = fullPriceQty * basePrice + discountedQty * discountedUnitPrice;
  const blendedPerUnit = ctx.quantity > 0 ? Math.round(blendedTotal / ctx.quantity) : basePrice;

  return { applies: true, newPrice: blendedPerUnit };
}

function evaluateCapacityBasedPricing(config: CapacityBasedPricingConfig, _basePrice: number, ctx: PricingContext): RuleEvalResult {
  if (ctx.currentSoldCount === undefined) return { applies: false };

  const sortedTiers = [...config.tiers].sort((a, b) => {
    if (a.upToSold === null) return 1;
    if (b.upToSold === null) return -1;
    return a.upToSold - b.upToSold;
  });
  const currentSpot = ctx.currentSoldCount + 1;
  const matchingTier = sortedTiers.find(
    (t) => t.upToSold === null || currentSpot <= t.upToSold,
  );

  if (!matchingTier) {
    const lastTier = sortedTiers[sortedTiers.length - 1];
    return lastTier ? { applies: true, newPrice: lastTier.price } : { applies: false };
  }

  return { applies: true, newPrice: matchingTier.price };
}

export function evaluateDeposit(config: DepositConfig, totalPrice: number): DepositEvalResult {
  const depositAmount =
    config.depositType === "percentage"
      ? Math.round((totalPrice * config.depositValue) / 100)
      : Math.min(config.depositValue, totalPrice);
  return { applies: true, depositAmount, remainderDue: config.remainderDue };
}

function evaluateSessionBundle(
  config: SessionBundleConfig,
  ctx: PricingContext
): { eligible: boolean; message?: string } {
  const sessions = ctx.selectedSessions;
  if (!sessions || sessions.length < config.minSessions) {
    return {
      eligible: false,
      message: `Select at least ${config.minSessions} sessions within ${config.timeWindowDays} days to unlock this ticket`,
    };
  }

  const dates = sessions.map((s) => new Date(s.date).getTime()).sort((a, b) => a - b);
  const spanDays = (dates[dates.length - 1] - dates[0]) / (1000 * 60 * 60 * 24);

  if (spanDays > config.timeWindowDays) {
    return {
      eligible: false,
      message: `All selected sessions must be within ${config.timeWindowDays} days of each other`,
    };
  }

  return { eligible: true };
}

function applyModification(basePrice: number, action: ModificationAction): number {
  switch (action.type) {
    case "percentage_decrease":
      return Math.max(0, Math.round(basePrice * (1 - action.value / 100)));
    case "percentage_increase":
      return Math.max(0, Math.round(basePrice * (1 + action.value / 100)));
    case "fixed_decrease":
      return Math.max(0, basePrice - action.value);
    case "fixed_increase":
      return Math.max(0, basePrice + action.value);
    case "absolute_override":
      return Math.max(0, action.value);
    default:
      return basePrice;
  }
}

function legacyDiscountToAction(
  discountType: DiscountType,
  discountValue: number,
  ctx: PricingContext,
): ModificationAction | null {
  switch (discountType) {
    case "percentage":
      return { type: "percentage_decrease", value: discountValue };
    case "fixed_per_unit":
      return { type: "fixed_decrease", value: discountValue };
    case "price_override":
      return { type: "absolute_override", value: discountValue };
    case "fixed_total": {
      const perUnit = ctx.quantity > 0 ? Math.round(discountValue / ctx.quantity) : 0;
      return { type: "fixed_decrease", value: perUnit };
    }
    default:
      return null;
  }
}

function evaluateConditionalDiscount(rule: PricingRule, basePrice: number, ctx: PricingContext): RuleEvalResult {
  const action =
    rule.modificationAction ??
    (rule.discountType && rule.discountValue != null
      ? legacyDiscountToAction(rule.discountType, rule.discountValue, ctx)
      : null);
  if (!action) return { applies: false };
  const newPrice = applyModification(basePrice, action);
  return { applies: true, newPrice };
}

function evaluateSingleRule(rule: PricingRule, basePrice: number, ctx: PricingContext): RuleEvalResult {
  const gate = evaluateConditionGroups(rule.conditionGroups, rule.conditions, ctx);
  if (!gate.passes) return { applies: false };

  const config = rule.config;
  switch (rule.ruleType) {
    case "CONDITIONAL_DISCOUNT":
      return evaluateConditionalDiscount(rule, basePrice, ctx);
    case "TIERED_PRICING":
      return evaluateTieredPricing(config as TieredPricingConfig, basePrice, ctx);
    case "VOLUME_DISCOUNT":
      return evaluateVolumeDiscount(config as VolumeDiscountConfig, basePrice, ctx);
    case "RENTAL_FRACTION_TIERS":
      return evaluateRentalFractionTiers(rule.tiers, basePrice, ctx);
    case "MULTI_SESSION":
      return evaluateMultiSession(config as MultiSessionConfig, basePrice, ctx);
    case "GROUP_SIZE":
      return evaluateGroupSize(config as GroupSizeConfig, basePrice, ctx);
    case "EARLY_BIRD":
      return evaluateEarlyBird(config as EarlyBirdConfig, basePrice, ctx);
    case "LAST_MINUTE":
      return evaluateLastMinute(config as LastMinuteConfig, basePrice, ctx);
    case "PEAK_TIME":
      return evaluatePeakTime(config as PeakTimeConfig, basePrice, ctx);
    case "MEMBER_PRICING":
      return evaluateMemberPricing(config as MemberPricingConfig, basePrice, ctx);
    case "SLIDING_SCALE":
      return evaluateSlidingScale(config as SlidingScaleConfig, basePrice, ctx);
    case "BOGO":
      return evaluateBogo(config as BogoConfig, basePrice, ctx);
    case "CAPACITY_BASED_PRICING":
      return evaluateCapacityBasedPricing(config as CapacityBasedPricingConfig, basePrice, ctx);
    default:
      return { applies: false };
  }
}

export function evaluatePricingRules(
  basePrice: number,
  rules: PricingRule[],
  context: PricingContext,
): PricingResult {
  const activeRules = rules
    .filter((r) => r.status === "enabled")
    .filter((r) => isWithinValidityWindow(r, context.bookingTime))
    .sort((a, b) => a.priority - b.priority);

  // Evaluate eligibility rules first (SESSION_BUNDLE)
  const eligibilityRules = activeRules.filter((r) => ELIGIBILITY_TYPES.has(r.ruleType));
  const failedEligibility: Array<{ ruleType: PricingRuleType; message: string }> = [];

  for (const rule of eligibilityRules) {
    if (rule.ruleType === "SESSION_BUNDLE") {
      const result = evaluateSessionBundle(rule.config as SessionBundleConfig, context);
      if (!result.eligible) {
        failedEligibility.push({
          ruleType: rule.ruleType,
          message: result.message!,
        });
      }
    }
  }

  if (failedEligibility.length > 0) {
    const breakdown = generateBreakdown(basePrice, basePrice, [], null, context.quantity);
    return {
      basePrice,
      finalPrice: basePrice,
      totalPerOrder: basePrice * context.quantity,
      depositAmount: null,
      depositRemainder: null,
      depositRemainderDue: null,
      appliedRules: [],
      breakdown,
      isSlidingScale: false,
      slidingScaleConfig: null,
      eligibility: {
        eligible: false,
        message: failedEligibility[0].message,
        failedRules: failedEligibility,
      },
    };
  }

  const isConditionalOverride = (r: PricingRule) => {
    if (r.ruleType !== "CONDITIONAL_DISCOUNT") return false;
    if (r.modificationAction) return r.modificationAction.type === "absolute_override";
    return r.discountType === "price_override";
  };

  // The override walk below is first-match-wins. A shape rule (tiered ladder,
  // group size, capacity bands) is unconditional and matches every basket, so
  // a conditional override such as "£50 on Fridays" — a modification the
  // partner layered on top of that shape — is tried BEFORE it: when its
  // conditions hold it replaces the shape's price, when they fail the shape
  // applies as before. Sorting both kinds by priority alone left the outcome
  // to load order — a Friday override created after the ticket's tiered rule
  // never fired, while the same rule on a ticket without a shape rule did.
  // Only overrides that are actually gated jump the queue: an override with no
  // conditions is "always £X", contradicts the shape outright, and keeps
  // competing with it by priority, so deploying this cannot silently switch a
  // live ladder off. Each half keeps its own priority order.
  const isGatedOverride = (r: PricingRule) =>
    isConditionalOverride(r) && hasEvaluableConditions(r.conditionGroups, r.conditions);
  const priceOverrideRules = [
    ...activeRules.filter(isGatedOverride),
    ...activeRules.filter(
      (r) => !isGatedOverride(r) && (isConditionalOverride(r) || PRICE_OVERRIDE_TYPES.has(r.ruleType)),
    ),
  ];
  const modifierRules = activeRules.filter((r) => {
    if (r.ruleType === "CONDITIONAL_DISCOUNT") return !isConditionalOverride(r);
    return DISCOUNT_TYPES.has(r.ruleType);
  });
  const paymentRules = activeRules.filter((r) => PAYMENT_TYPES.has(r.ruleType));

  let currentPrice = basePrice;
  let currentExactTotal = basePrice * context.quantity;
  let chargedQuantity = context.quantity;
  const appliedRules: AppliedRule[] = [];
  let isSlidingScale = false;
  let slidingScaleConfig: SlidingScaleConfig | null = null;

  for (const rule of priceOverrideRules) {
    const result = evaluateSingleRule(rule, currentPrice, context);
    if (result.applies) {
      currentPrice = result.newPrice;
      currentExactTotal = result.exactTotal ?? result.newPrice * context.quantity;
      chargedQuantity = result.chargedQuantity ?? context.quantity;
      appliedRules.push({
        ruleId: rule.id,
        ruleName: rule.name,
        ruleType: rule.ruleType,
        adjustment: result.newPrice - basePrice,
        description: rule.description ?? rule.name,
        tieredBreakdown: result.tieredBreakdown,
      });
      if (result.isCustomerChoice) {
        isSlidingScale = true;
        slidingScaleConfig = result.slidingScaleConfig ?? null;
      }
      break;
    }
  }

  let bestNonStackableDiscount = 0;
  let bestNonStackableRule: PricingRule | null = null;
  let stackedAdjustmentTotal = 0;

  for (const rule of modifierRules) {
    const result = evaluateSingleRule(rule, currentPrice, context);
    if (!result.applies) continue;

    const adjustment = result.newPrice - currentPrice;
    if (adjustment === 0) continue;

    if (adjustment > 0) {
      stackedAdjustmentTotal += adjustment;
      appliedRules.push({
        ruleId: rule.id,
        ruleName: rule.name,
        ruleType: rule.ruleType,
        adjustment,
        description: rule.description ?? rule.name,
      });
      continue;
    }

    const discount = -adjustment;
    if (rule.stackable) {
      stackedAdjustmentTotal += adjustment;
      appliedRules.push({
        ruleId: rule.id,
        ruleName: rule.name,
        ruleType: rule.ruleType,
        adjustment,
        description: rule.description ?? rule.name,
      });
    } else if (discount > bestNonStackableDiscount) {
      bestNonStackableDiscount = discount;
      bestNonStackableRule = rule;
    }
  }

  if (bestNonStackableRule) {
    appliedRules.push({
      ruleId: bestNonStackableRule.id,
      ruleName: bestNonStackableRule.name,
      ruleType: bestNonStackableRule.ruleType,
      adjustment: -bestNonStackableDiscount,
      description: bestNonStackableRule.description ?? bestNonStackableRule.name,
    });
  }

  const totalAdjustment = stackedAdjustmentTotal - bestNonStackableDiscount;
  currentPrice = Math.max(0, currentPrice + totalAdjustment);
  currentExactTotal = Math.max(0, currentExactTotal + totalAdjustment * context.quantity);

  let depositAmount: number | null = null;
  let depositRemainder: number | null = null;
  let depositRemainderDue: string | null = null;

  for (const rule of paymentRules) {
    const result = evaluateDeposit(rule.config as DepositConfig, currentPrice);
    if (result.applies) {
      depositAmount = result.depositAmount;
      depositRemainder = currentPrice - result.depositAmount;
      depositRemainderDue = result.remainderDue;
      appliedRules.push({
        ruleId: rule.id,
        ruleName: rule.name,
        ruleType: rule.ruleType,
        adjustment: 0,
        description: rule.description ?? rule.name,
      });
      break;
    }
  }

  const breakdown = generateBreakdown(basePrice, currentPrice, appliedRules, depositAmount, context.quantity, currentExactTotal);

  return {
    basePrice,
    finalPrice: currentPrice,
    totalPerOrder: currentExactTotal,
    chargedQuantity,
    depositAmount,
    depositRemainder,
    depositRemainderDue,
    appliedRules,
    breakdown,
    isSlidingScale,
    slidingScaleConfig,
    eligibility: eligibilityRules.length > 0 ? { eligible: true } : undefined,
  };
}
